import React, { createContext, useContext, useEffect, useState } from "react";
import staticAgents from "../../assets/templates/agents.json";
import staticMaps from "../../assets/templates/maps.json";

const GameDataContext = createContext({ agents: {}, maps: {} });

const CACHE_KEY = "valorant-game-data";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export const GameDataProvider = ({ children }) => {
  const [agents, setAgents] = useState({});
  const [maps, setMaps] = useState({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const {
        agents: cachedAgents,
        maps: cachedMaps,
        timestamp,
      } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL) {
        setAgents(cachedAgents);
        setMaps(cachedMaps);
        setIsReady(true);
        return;
      }
    }

    Promise.all([
      fetch("https://valorant-api.com/v1/agents?isPlayableCharacter=true").then(
        (r) => r.json()
      ),
      fetch("https://valorant-api.com/v1/maps").then((r) => r.json()),
    ])
      .then(([agentsRes, mapsRes]) => {
        const agentsMap = agentsRes.data.reduce((acc, agent) => {
          acc[agent.uuid] = {
            displayName: agent.displayName,
            displayIcon: agent.displayIcon,
            portrait: agent.fullPortrait,
          };
          return acc;
        }, {});

        const mapsMap = mapsRes.data.reduce((acc, map) => {
          if (map.mapUrl) {
            acc[map.mapUrl] = {
              displayName: map.displayName,
              listView: map.listViewIcon,
              splash: map.splash,
            };
          }
          return acc;
        }, {});

        setAgents(agentsMap);
        setMaps(mapsMap);
        setIsReady(true);
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            agents: agentsMap,
            maps: mapsMap,
            timestamp: Date.now(),
          })
        );
      })
      .catch(() => {
        // API failed — fall back to static files so the app still works
        setAgents(staticAgents);
        setMaps(staticMaps);
        setIsReady(true);
      });
  }, []);

  return (
    <GameDataContext.Provider value={{ agents, maps }}>
      {isReady ? children : null}
    </GameDataContext.Provider>
  );
};

export const useGameData = () => useContext(GameDataContext);
